import styled from 'styled-components';

import { CopyValue } from 'components/CopyValue';
import { DetailExternalLink, DetailLink, DetailValue } from 'components/DetailsView';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { type GetTicketQuery } from 'data/__generated__';

import { TicketStatusTag } from '../components/TicketStatusTag';
import {
    CRITICALITY_LABELS,
    PRIORITY_LABELS,
    displayValue,
    formatOdooRef,
    formatTicketDatetime,
    getOdooTicketUrl,
    htmlToText,
} from '../helpers';

type Ticket = GetTicketQuery['ticket'];

export const TicketGeneralSection = ({ ticket }: { ticket: Ticket }) => {
    const description = htmlToText(ticket.description);

    return (
        <SectionContainer title="Informations générales" keepOpen>
            <>
                <ColumnsSectionContainer numberOfColumns={3}>
                    <SectionColumn>
                        <CopyValue label="Id Totem" value={ticket._id} />
                        <DetailValue label="Référence Odoo" value={ticket.odooRef ?? String(ticket.odooId)} />
                        <DetailValue label="Id Odoo" value={ticket.odooId} />
                        <DetailExternalLink
                            label="Ticket Odoo"
                            href={getOdooTicketUrl(ticket.odooId)}
                            name={`Helpdesk #${ticket.odooRef ?? ticket.odooId}`}
                            iconSrc="https://odoo.neocorner.fr/web/static/img/favicon.ico"
                            iconAlt="Logo Odoo"
                            dataTest="ticket-odoo-link"
                        />
                    </SectionColumn>
                    <SectionColumn>
                        <DetailValue label="Intitulé" value={<span data-test="ticket-name">{ticket.name}</span>} />
                        <DetailValue label="Statut" value={<TicketStatusTag isClosed={ticket.isClosed} />} />
                        <DetailValue label="Actif" value={displayValue(ticket.active)} />
                        <DetailValue label="Externe" value={displayValue(ticket.isExternal)} />
                    </SectionColumn>
                    <SectionColumn>
                        <DetailValue label="Priorité" value={PRIORITY_LABELS[ticket.priority] ?? ticket.priority} />
                        <DetailValue
                            label="Criticité"
                            value={
                                ticket.ticketCriticality
                                    ? (CRITICALITY_LABELS[ticket.ticketCriticality] ?? ticket.ticketCriticality)
                                    : '—'
                            }
                        />
                        <DetailValue label="État kanban" value={displayValue(ticket.kanbanState)} />
                        <DetailValue label="Date d'intervention" value={displayValue(ticket.interventionDate)} />
                        <DetailValue label="Id ticket AVA" value={displayValue(ticket.avaTicketId)} />
                    </SectionColumn>
                </ColumnsSectionContainer>
                <DescriptionBlock>
                    <DetailValue
                        label="Description"
                        value={<DescriptionText data-test="ticket-description">{description || '—'}</DescriptionText>}
                    />
                </DescriptionBlock>
            </>
        </SectionContainer>
    );
};

export const TicketLocationSection = ({ ticket }: { ticket: Ticket }) => {
    return (
        <SectionContainer title="Localisation" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <DetailValue label="Organisation" value={ticket.microstoreColumn?.site.organizationName ?? '—'} />
                    {ticket.microstoreColumn?.siteId ? (
                        <DetailLink
                            label="Site Totem"
                            path="site"
                            name={ticket.microstoreColumn.siteName}
                            value={ticket.microstoreColumn.siteId}
                        />
                    ) : (
                        <DetailValue label="Site Totem" value="—" />
                    )}
                    <DetailValue label="Site Odoo" value={formatOdooRef(ticket.site)} />
                    <DetailValue label="Emplacement Odoo" value={formatOdooRef(ticket.emplacement)} />
                </SectionColumn>
                <SectionColumn>
                    {ticket.microstoreColumn?._id ? (
                        <DetailLink
                            label="Colonne Totem"
                            path="microstore-column"
                            name={ticket.microstoreColumn.name}
                            value={ticket.microstoreColumn._id}
                        />
                    ) : (
                        <DetailValue label="Colonne Totem" value="—" />
                    )}
                    <DetailValue label="Groupe de colonnes" value={ticket.microstoreColumn?.columnGroupName ?? '—'} />
                    <DetailValue label="Machine Odoo" value={formatOdooRef(ticket.machine)} />
                    <DetailValue
                        label="Id machine Totem (Odoo)"
                        value={displayValue(ticket.machine?.totadminMachineId)}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Partenaire Odoo" value={formatOdooRef(ticket.partner)} />
                    <DetailValue label="Email partenaire" value={displayValue(ticket.partnerEmail)} />
                    <DetailValue label="Téléphone partenaire" value={displayValue(ticket.partnerPhone)} />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

export const TicketTrackingSection = ({ ticket }: { ticket: Ticket }) => {
    const tagsLabel = ticket.tags.length > 0 ? ticket.tags.map((tag) => `${tag.name} (#${tag.id})`).join(', ') : '—';

    return (
        <SectionContainer title="Suivi" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <DetailValue
                        label="Étape"
                        value={<span data-test="ticket-stage">{formatOdooRef(ticket.stage)}</span>}
                    />
                    <DetailValue label="Étape repliée (terminé)" value={displayValue(ticket.stage?.fold)} />
                    <DetailValue label="Équipe" value={formatOdooRef(ticket.team)} />
                    <DetailValue label="Type" value={formatOdooRef(ticket.ticketType)} />
                    <DetailValue label="Canal" value={formatOdooRef(ticket.channel)} />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Intervenant Odoo" value={formatOdooRef(ticket.user)} />
                    <DetailValue label="Login intervenant" value={displayValue(ticket.user?.login)} />
                    {ticket.operator?._id ? (
                        <DetailLink
                            label="Opérateur Totem"
                            path="user"
                            name={ticket.operator.fullname}
                            value={ticket.operator._id}
                        />
                    ) : (
                        <DetailValue label="Opérateur Totem" value="—" />
                    )}
                    <DetailValue label="Id opérateur" value={displayValue(ticket.operatorId)} />
                </SectionColumn>
                <SectionColumn>
                    {ticket.siteSetupId ? (
                        <DetailLink
                            label="Rangement"
                            path="site-setup"
                            name={ticket.siteSetupId}
                            value={ticket.siteSetupId}
                        />
                    ) : (
                        <DetailValue label="Rangement" value="—" />
                    )}
                    <DetailValue label="Tags Odoo" value={tagsLabel} />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

export const TicketDatesSection = ({ ticket }: { ticket: Ticket }) => {
    return (
        <SectionContainer title="Dates" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <DetailValue label="Créé dans Odoo" value={formatTicketDatetime(ticket.odooCreateDate)} />
                    <DetailValue label="Date d'assignation" value={formatTicketDatetime(ticket.assignDate)} />
                    <DetailValue label="Échéance" value={formatTicketDatetime(ticket.dateDeadline)} />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Échéance SLA" value={formatTicketDatetime(ticket.slaDeadline)} />
                    <DetailValue label="Date de clôture" value={formatTicketDatetime(ticket.closeDate)} />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Arrivé dans Totem" value={formatTicketDatetime(ticket.createdAt)} />
                    <DetailValue label="Mis à jour dans Totem" value={formatTicketDatetime(ticket.updatedAt)} />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const DescriptionBlock = styled.div`
    margin-top: 20px;
`;

const DescriptionText = styled.span`
    white-space: pre-wrap;
    line-height: 1.4;
`;
